import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Helmet } from "react-helmet";
import Grid from "@mui/material/Grid2";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "../Footer";
import AboutUs from "../About";
import { ThemeProvider } from "@mui/material/styles";
import fastFoodTheme from "./fastFoodTheme";
import SpinGame from "./SpinGame";
import ThisOrThat from "../Games/ThisOrThat";
import Match3Game from "./Match3Game";
import BaseMemoryGame from "../../games/Memory/BaseMemoryGame";

// Sample game previews
const GAME_PREVIEWS = [
  {
    key: "wheel",
    title: "Wheel of Cravings",
    description:
      "Custom spin games that reward customers with deals and points",
    emoji: "🎡",
    color: "#FF69B4",
  },
  {
    key: "this-or-that",
    title: "This or That",
    description: "Engaging choice games featuring your menu items",
    emoji: "🍔",
    color: "#FF8C00",
  },
  {
    key: "match-3",
    title: "Match-3 with Menu Icons",
    description: "Classic match-3 games themed with your brand's menu",
    emoji: "🍟",
    color: "#FFD700",
  },
];

const FOOD_PAIRS = [
  { left: "🍔 Comet Burger", right: "🍗 Chicken Bites" },
  { left: "🍟 Curly Fries", right: "🧀 Cheese Fries" },
  { left: "🥤 Rocket Soda", right: "🧃 Orange Blast" },
  { left: "🍕 Supreme Pizza", right: "🌮 Crunch Taco" },
  { left: "🍦 Milky Sundae", right: "🥧 Star Pie" },
];

// Food emoji set for FoodMemoryGame
const FOOD_MEMORY_ITEMS = [
  { id: 1, title: "Burger", image: "🍔" },
  { id: 2, title: "Fries", image: "🍟" },
  { id: 3, title: "Pizza", image: "🍕" },
  { id: 4, title: "Chicken", image: "🍗" },
  { id: 5, title: "Taco", image: "🌮" },
  { id: 6, title: "Ice Cream", image: "🍦" },
  { id: 7, title: "Soda", image: "🥤" },
  { id: 8, title: "Sandwich", image: "🥪" },
];

function FoodMemoryGame() {
  return (
    <BaseMemoryGame
      title="Food Memory"
      subtitle="Match the food name to its emoji!"
      shareText="I just played Food Memory on Flying Comet Games!"
      shareUrl={window.location.href}
      successBackgroundColor="#ECB061"
      shareButtonColor={["#ECB061", "#555"]}
      customItems={FOOD_MEMORY_ITEMS}
      publicationId="FoodMemoryGame"
      hideTitle={false}
      showDatePicker={false}
    />
  );
}

const FastFoodHome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedGame, setSelectedGame] = useState("this-or-that");

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <ThemeProvider theme={fastFoodTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Helmet>
          <title>
            Custom Games for Fast Food & Coffee Brands – Flying Comet Games
          </title>
          <meta
            name="description"
            content="Boost loyalty and repeat visits with custom branded games. Flying Comet creates snack-sized games that keep customers coming back—no dev work required."
          />
        </Helmet>

        {/* Hero Section */}
        <Box
          sx={{
            background: "#FCD2AE",
            color: "black",
            pt: { xs: 8, md: 12 },
            pb: { xs: 12, md: 16 },
            position: "relative",
          }}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                maxWidth: { xs: "100%", md: "1200px" },
                mx: "auto",
                px: { xs: 2, md: 4 },
                position: "relative",
                zIndex: 2,
              }}
            >
              <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                <Grid item size={{ xs: 12, md: 7 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "2.2rem", md: "4.5rem" },
                      fontWeight: 800,
                      mb: 1,
                      lineHeight: 1.1,
                      color: "black",
                    }}
                  >
                    Turn cravings into daily check-ins
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 400,
                      mb: 4,
                      opacity: 0.9,
                      color: "black",
                      maxWidth: "600px",
                    }}
                  >
                    Don't miss out on repeat visits—our white-labeled games
                    boost loyalty and customer engagement for restaurants and
                    coffee shops.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      mb: 4,
                      opacity: 0.8,
                      color: "black",
                      maxWidth: "600px",
                      fontStyle: "italic",
                    }}
                  >
                    Trusted by digital teams at publications like{" "}
                    <a
                      href="https://www.seattletimes.com/games-seattle-wordy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#FF6B6B", textDecoration: "underline" }}
                    >
                      The Seattle Times
                    </a>{" "}
                    — now bringing that magic to food brands.
                  </Typography>
                  <Button
                    variant="contained"
                    href="https://cal.com/calli-fuchigami/15min"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "white",
                      color: "#FF6B6B",
                      px: 6,
                      py: 3,
                      borderRadius: "100px",
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                      minHeight: "2.5em",
                      mb: { xs: 6, md: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.95)",
                        color: "#FF6B6B",
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 20px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    Get a custom game preview →
                  </Button>
                </Grid>

                <Grid item size={{ xs: 12, md: 5 }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Match3Game />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>

          {/* Decorative background elements */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background:
                "radial-gradient(circle at 20% 150%, #FF6B6B 0%, transparent 50%)",
            }}
          />
        </Box>

        {/* Value Prop Section */}
        <Box
          py={12}
          sx={{
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,107,107,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 2,
                  textAlign: "center",
                  color: "black",
                }}
              >
                Not just games.{" "}
                <span style={{ fontStyle: "italic" }}>Customer magnets.</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: 500,
                  mb: 5,
                  textAlign: "center",
                  color: "black",
                  opacity: 0.85,
                }}
              >
                Games that drive check-ins, boost loyalty program usage, and
                bring customers back for more.
              </Typography>

              <Grid
                container
                spacing={4}
                sx={{ mb: 4 }}
                justifyContent="center"
              >
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 5,
                      minHeight: 260,
                      width: { xs: "100%", md: 270 },
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "#FF6B6B",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3} minHeight={350}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#FF6B6B",
                        }}
                      >
                        🍟 Loyalty, Supercharged
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: "black",
                        }}
                      >
                        Daily games your customers play to unlock points, deals,
                        or just for fun. More play = more orders.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: "#FF8C00",
                        borderRadius: "0 0 10px 10px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 5,
                      minHeight: 260,
                      width: { xs: "100%", md: 270 },
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "#FF8C00",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3} minHeight={350}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#FF8C00",
                        }}
                      >
                        📱 Built Into Your App or Website
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: "black",
                        }}
                      >
                        No downloads needed. Embed seamlessly in your existing
                        mobile app or ordering site.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: "#FF8C00",
                        borderRadius: "0 0 10px 10px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 5,
                      minHeight: 260,
                      width: { xs: "100%", md: 270 },
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "black",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3} minHeight={350}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "black",
                        }}
                      >
                        ⚡ Ready in 7 Days
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: "black",
                        }}
                      >
                        Your first custom game, themed for your menu or brand,
                        live in a week.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: "#FF8C00",
                        borderRadius: "0 0 10px 10px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* How it Works Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#FFD1A1", // orange box background
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,107,107,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 6,
                  textAlign: "center",
                  color: "black",
                }}
              >
                How it works
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "3rem",
                        mb: 2,
                      }}
                    >
                      1️⃣
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#FF6B6B",
                      }}
                    >
                      Choose a game
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      Pick from proven formats like scratch cards, spins, or
                      match-3 games.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "3rem",
                        mb: 2,
                      }}
                    >
                      2️⃣
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#FF8C00",
                      }}
                    >
                      We brand and launch it
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      Your game is customized with your menu, brand, and goals.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "3rem",
                        mb: 2,
                      }}
                    >
                      3️⃣
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "black",
                      }}
                    >
                      Embed in your app or site
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      A drop-in web game—no engineering work needed.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Games Preview Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#FFF5F5",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,107,107,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Box mb={8} sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "2.5rem", md: "4rem" },
                    fontWeight: 800,
                    mb: 3,
                    color: "#FF6B6B",
                    position: "relative",
                    display: "inline-block",
                  }}
                >
                  Custom Games That Drive Visits
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.3rem", md: "1.5rem" },
                    maxWidth: "800px",
                    mx: "auto",
                    mt: 4,
                    color: "black",
                    lineHeight: 1.8,
                  }}
                >
                  We'll create custom games that match your brand's style and
                  menu items
                </Typography>
              </Box>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="stretch"
                sx={{
                  flexWrap: "wrap",
                  gap: { xs: 4, md: 0 },
                }}
              >
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                      mb: 5,
                    }}
                  >
                    <Box aria-label="Spin the wheel game with food prizes">
                      <SpinGame />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        mt: 3,
                        fontWeight: 700,
                        color: "#FF69B4",
                        fontSize: "1.35rem",
                      }}
                    >
                      Wheel of Cravings
                    </Typography>
                    <Typography
                      sx={{
                        color: "black",
                        fontSize: "1.1rem",
                        mt: 1,
                        textAlign: "center",
                      }}
                    >
                      Custom spin games that reward customers with deals and
                      points
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  item
                  size={{ xs: 12, md: 6 }}
                  sx={{ height: "100%" }}
                  mb={3}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Box
                      aria-label="Match-3 puzzle game using burger and fries icons"
                      sx={{
                        minHeight: { xs: 340, md: 420 },
                        width: { xs: "100%", md: 360 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <Match3Game />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        mt: 1,
                        fontWeight: 700,
                        color: "black",
                        fontSize: "1.35rem",
                        textAlign: "center",
                      }}
                    >
                      Menu Match-3
                    </Typography>
                    <Typography
                      sx={{
                        color: "black",
                        fontSize: "1.1rem",
                        mt: 1,
                        textAlign: "center",
                      }}
                    >
                      Classic puzzle game, customized with your brand's food
                      items.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                      border: "4px solid #FF6B6B",
                      borderRadius: "24px",
                      p: 3,
                      background: "#FFF5F5",
                      minHeight: { xs: 340, md: 420 },
                      mt: 3,
                    }}
                  >
                    <Box
                      sx={{ width: "100%" }}
                      aria-label="This or That game with food choices like Supreme Pizza or Crunch Taco"
                    >
                      <ThisOrThat
                        pairs={FOOD_PAIRS}
                        brandColors={{
                          primary: "#FF8C00",
                          secondary: "#FF6B6B",
                          background: "#FFF5F5",
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 3,
                      fontWeight: 700,
                      color: "#FF8C00",
                      fontSize: "1.35rem",
                      textAlign: "center",
                    }}
                  >
                    This or That
                  </Typography>
                  <Typography
                    sx={{
                      color: "black",
                      fontSize: "1.1rem",
                      mt: 1,
                      textAlign: "center",
                    }}
                  >
                    Engaging choice games featuring your menu items
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                      backgroundColor: "transparent",
                      mt: 3,
                    }}
                  >
                    <Box
                      aria-label="Memory game with food emojis"
                      sx={{
                        backgroundColor: "#FFF",
                        border: "4px solid #ECB061",
                        borderRadius: "24px",
                        boxShadow: "0 4px 12px rgba(236,176,97,0.10)",
                        minHeight: { xs: 340, md: 420 },
                        width: { xs: "100%", md: 360 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        mb: 2,
                      }}
                    >
                      <FoodMemoryGame />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        mt: 1,
                        fontWeight: 700,
                        color: "#ECB061",
                        fontSize: "1.35rem",
                      }}
                    >
                      Food Memory
                    </Typography>
                    <Typography
                      sx={{
                        color: "black",
                        fontSize: "1.1rem",
                        mt: 1,
                        textAlign: "center",
                      }}
                    >
                      Match food names to their emoji! Fun for all ages.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#FFF5F5",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,107,107,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 6,
                  textAlign: "center",
                  color: "#FF6B6B",
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 4,
                  color: "black",
                  lineHeight: 1.6,
                  textAlign: "center",
                  fontWeight: 400,
                  opacity: 0.9,
                }}
              >
                Still curious? Here are quick answers to common partner
                questions.
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#FF6B6B",
                      }}
                    >
                      Does this require any changes to our app or website?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      Nope! Our games are lightweight web components that embed
                      directly—no dev lift required.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#FF8C00",
                      }}
                    >
                      Can we customize the game with our own brand or menu?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      Yes. Every game is fully branded with your colors, tone,
                      and menu items. It feels native to your experience.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "black",
                      }}
                    >
                      What kinds of results can we expect?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      Our games are designed to increase daily check-ins, boost
                      loyalty usage, and give your customers a reason to come
                      back.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "10px",
                      p: 4,
                      height: "100%",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#FF69B4",
                      }}
                    >
                      How fast can we launch?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: "black",
                      }}
                    >
                      We deliver your first custom game in 7 days or less.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Final CTA Section */}
        <Box
          py={12}
          sx={{
            background: "#FCD2AE",
            color: "black",
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4} textAlign="center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 3,
                  color: "black",
                }}
              >
                Games that bring customers back.
                <br />
                Loyalty that creates raving fans.
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  mb: 6,
                  maxWidth: "600px",
                  mx: "auto",
                  color: "black",
                  opacity: 0.9,
                }}
              >
                We'll create a custom game that keeps your customers coming back
                for more—both for your food and for the fun.
              </Typography>
              <Button
                variant="contained"
                href="https://cal.com/calli-fuchigami/15min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: "white",
                  color: "#FF6B6B",
                  px: { xs: 4, md: 6 },
                  py: { xs: 1.5, md: 2 },
                  borderRadius: "100px",
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  minHeight: "2.5em",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color: "#FF6B6B",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 20px rgba(0,0,0,0.15)",
                  },
                }}
              >
                Get a custom game preview →
              </Button>
            </motion.div>
          </Box>
        </Box>

        <AboutUs isComponent={true} />

        {/* Bottom CTA */}
        <Box
          py={8}
          sx={{
            backgroundColor: "#FCD2AE",
            textAlign: "center",
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <Button
              variant="contained"
              href="https://cal.com/calli-fuchigami/15min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: "white",
                color: "#FF6B6B",
                px: { xs: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                borderRadius: "100px",
                fontSize: { xs: "1.1rem", md: "1.2rem" },
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 8px 24px rgba(255,107,107,0.15)",
                minHeight: "2.5em",
                transition: "all 0.3s ease-in-out",
                border: "1px solid rgba(255,107,107,0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "#FF6B6B",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 28px rgba(255,107,107,0.2)",
                },
              }}
            >
              Book a quick call — see your brand in a live game →
            </Button>
          </Box>
        </Box>

        <Footer bgColor="white" />
      </Box>
    </ThemeProvider>
  );
};

export default FastFoodHome;
